import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CircularProgress,
  Grid,
  Box,
  ButtonBase,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import PieChart from "./PieChart";
import UsageCard from "./UsageCard";
import axios from "axios";
import { useNavigate } from "react-router-dom";

function RegionCard({ region }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lineChartData, setLineChartData] = useState([]);
  const [clicked, setClicked] = useState(false);
  const [cardRect, setCardRect] = useState(null);
  const cardRef = useRef(null);

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setCardRect(rect);
    }
    setClicked(true);

    setTimeout(() => {
      navigate(`/area-dashboard/${region.region_id}`);
    }, 600); // match animation duration
  };

  useEffect(() => {
    const fetchSites = async () => {
      setLoading(true);
      setError(null);
      try {
        const sitesResponse = await fetch(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/sites?region_id=${region.region_id}`,
          {
            method: "GET",
            headers: { Authorization: `${token}` },
          }
        );

        if (!sitesResponse.ok)
          throw new Error(`Failed to fetch sites: ${sitesResponse.status}`);
        const sitesData = await sitesResponse.json();
        const sites = Array.isArray(sitesData) ? sitesData : [];

        const enrichedSites = await Promise.all(
          sites.map(async (site) => {
            try {
              const gatewayResponse = await fetch(
                `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/gateway/profile?site_id=${site.site_id}`,
                { headers: { Authorization: `${token}` } }
              );
              const gatewayData = await gatewayResponse.json();
              const gatewayName = gatewayData[0]?.name;
              if (!gatewayName) return null;

              const lambdaResponse = await axios.get(
                "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/query-timestream",
                {
                  headers: { Authorization: `${token}` },
                  params: {
                    thing_name: gatewayName,
                    query_type: "area_dashboard",
                  },
                }
              );
              const { daily, rap_total_month, bit_total_month } =
                lambdaResponse.data;

              return {
                id: site.site_id,
                name: site.name,
                gatewayName,
                Asphalt_Day: parseFloat(daily[0] || 0),
                RAP_Total_Day: parseFloat(daily[1] || 0),
                Bit_Total: parseFloat(daily[2] || 0),
                Fuel_Total: parseFloat(daily[3] || 0),
                RAP_Total: parseFloat(daily[4] || 0),
                Asphalt_Year: parseFloat(daily[5] || 0),
                Breakdown_Hrs: parseFloat(daily[6] || 0),
                Offline_Hrs: parseFloat(daily[7] || 0),
                Idle_Hrs: parseFloat(daily[8] || 0),
                Production_Hrs: parseFloat(daily[9] || 0),
                Electric_Total: parseFloat(daily[10] || 0),
                Gas_Total: parseFloat(daily[11] || 0),
                RAP_Total_Month: rap_total_month || Array(12).fill(0),
                Bit_Total_Month: bit_total_month || Array(12).fill(0),
              };
            } catch (error) {
              console.error(`Error fetching data for site ${site.site_id}:`, error);
              return null;
            }
          })
        );

        const successfulSites = enrichedSites.filter((site) => site !== null);
        setSites(successfulSites);

        setLineChartData(
          successfulSites.map((site) => ({
            name: site.name,
            data: Array.isArray(site.Bit_Total_Month)
              ? site.Bit_Total_Month
              : Array(12).fill(0),
          }))
        );
      } catch (error) {
        setError(error.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, [region.region_id, token]);

  // Aggregate data
  const aggregateData = {
    Asphalt_Year: sites.reduce((sum, site) => sum + (site.Asphalt_Year || 0), 0),
    Asphalt_Day: sites.reduce((sum, site) => sum + (site.Asphalt_Day || 0), 0),
    RAP_Total_Day: sites.reduce((sum, site) => sum + (site.RAP_Total_Day || 0), 0),
    Fuel_Total: sites.reduce((sum, site) => sum + (site.Fuel_Total || 0), 0),
    Gas_Total: sites.reduce((sum, site) => sum + (site.Gas_Total || 0), 0),
    Electric_Total: sites.reduce((sum, site) => sum + (site.Electric_Total || 0), 0),
    Production_Hrs: sites.reduce((sum, site) => sum + (site.Production_Hrs || 0), 0),
    Idle_Hrs: sites.reduce((sum, site) => sum + (site.Idle_Hrs || 0), 0),
    Breakdown_Hrs: sites.reduce((sum, site) => sum + (site.Breakdown_Hrs || 0), 0),
    Offline_Hrs: sites.reduce((sum, site) => sum + (site.Offline_Hrs || 0), 0),
    RAP_Total_Month:
      sites.length > 0
        ? sites[0].RAP_Total_Month.map((_, i) =>
            sites.reduce((sum, site) => sum + (site.RAP_Total_Month[i] || 0), 0)
          )
        : Array(12).fill(0),
    Bit_Total_Month:
      sites.length > 0
        ? sites[0].Bit_Total_Month.map((_, i) =>
            sites.reduce((sum, site) => sum + (site.Bit_Total_Month[i] || 0), 0)
          )
        : Array(12).fill(0),
  };

  return (
    <>
      <Card
        ref={cardRef}
        sx={{
          height: "100%",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        <ButtonBase
          onClick={handleCardClick}
          sx={{
            width: "100%",
            height: "100%",
            textAlign: "left",
            padding: 2,
          }}
        >
          <VuiBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <VuiTypography variant="h6" color="white">
                  {region.name}
                </VuiTypography>
              </Grid>

              {loading ? (
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  </Box>
                </Grid>
              ) : error ? (
                <Grid item xs={12}>
                  <VuiTypography sx={{ color: "#ff5252" }}>{error}</VuiTypography>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6} md={4}>
                    <PieChart
                      Production_Hrs={aggregateData.Production_Hrs}
                      Idle_Hrs={aggregateData.Idle_Hrs}
                      Breakdown_Hrs={aggregateData.Breakdown_Hrs}
                      Offline_Hrs={aggregateData.Offline_Hrs}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <UsageCard
                      title="Daily Asphalt"
                      color="#0075FF"
                      value={Number(aggregateData.Asphalt_Day).toFixed(2)}
                      maxValue={0.1}
                      unit="T"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <UsageCard
                      title="Daily RAP"
                      color="#D5ED9F"
                      value={Number(aggregateData.RAP_Total_Day).toFixed(2)}
                      maxValue={0.1}
                      unit="T"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <UsageCard
                      title="Fuel"
                      color="#CAB773"
                      value={Number(aggregateData.Fuel_Total)}
                      maxValue={0.1}
                      unit="L"
                      tooltip="Yearly"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <UsageCard
                      title="Gas"
                      color="#73CAC5"
                      value={Number(aggregateData.Gas_Total)}
                      maxValue={0.1}
                      unit="kW"
                      tooltip="Yearly"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <UsageCard
                      title="Electricity"
                      color="#F0F477"
                      value={Number(aggregateData.Electric_Total)}
                      maxValue={0.1}
                      unit="kW"
                      tooltip="Yearly"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </VuiBox>
        </ButtonBase>
      </Card>

      <AnimatePresence>
        {clicked && cardRect && (
          <motion.div
      initial={{
        position: "fixed",
        top: cardRect.top,
        left: cardRect.left,
        width: cardRect.width,
        height: cardRect.height,
        opacity: 1,
        zIndex: 1000,
      }}
      animate={{
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        opacity: 0,
      }}
      exit={{
        opacity: 0,
      }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
      style={{
        background: "#1e1e2f", // match your card background
        borderRadius: 8,
        pointerEvents: "none",
      }}
          >
            {/* Expanded card content */}
            <VuiBox p={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <VuiTypography variant="h6" color="white">
                    {region.name}
                  </VuiTypography>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <PieChart
                    Production_Hrs={aggregateData.Production_Hrs}
                    Idle_Hrs={aggregateData.Idle_Hrs}
                    Breakdown_Hrs={aggregateData.Breakdown_Hrs}
                    Offline_Hrs={aggregateData.Offline_Hrs}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <UsageCard
                    title="Daily Asphalt"
                    color="#0075FF"
                    value={Number(aggregateData.Asphalt_Day).toFixed(2)}
                    maxValue={0.1}
                    unit="T"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <UsageCard
                    title="Daily RAP"
                    color="#D5ED9F"
                    value={Number(aggregateData.RAP_Total_Day).toFixed(2)}
                    maxValue={0.1}
                    unit="T"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <UsageCard
                    title="Fuel"
                    color="#CAB773"
                    value={Number(aggregateData.Fuel_Total)}
                    maxValue={0.1}
                    unit="L"
                    tooltip="Yearly"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <UsageCard
                    title="Gas"
                    color="#73CAC5"
                    value={Number(aggregateData.Gas_Total)}
                    maxValue={0.1}
                    unit="kW"
                    tooltip="Yearly"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <UsageCard
                    title="Electricity"
                    color="#F0F477"
                    value={Number(aggregateData.Electric_Total)}
                    maxValue={0.1}
                    unit="kW"
                    tooltip="Yearly"
                  />
                </Grid>
              </Grid>
            </VuiBox>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default RegionCard;